import api from './api';
import type { OrderItemStatus } from '@/types/orderStatus';
import {
  computeFileSha256,
  getUploadContentType,
  uploadFileToSignedUrl,
} from './uploadService';
import type { ShippingAddress } from '@/lib/shippingAddress';
import type { WorkspaceDraft } from '@/services/workspaceDraftService';

export type EtsyShop = {
  readonly id: string;
  readonly etsyShopId: string;
  readonly shopName: string;
  readonly shopUrl: string | null;
  readonly isActive: boolean;
  readonly connectedAt: string;
  readonly lastSyncAt: string | null;
};

export type EtsySyncLogStatus = 'SUCCESS' | 'PARTIAL' | 'FAILED';

export type EtsyPendingOrderData = {
  readonly receipt_id: number;
  readonly buyer_email: string;
  readonly name: string;
  readonly city: string;
  readonly state?: string;
  readonly zip?: string;
  readonly country_iso: string;
  readonly is_paid: boolean;
  readonly is_shipped: boolean;
  readonly paid_timestamp?: number;
  readonly payment_timestamp?: number;
  readonly create_timestamp?: number;
  readonly created_timestamp?: number;
  readonly formatted_address?: string;
  readonly first_line?: string;
  readonly second_line?: string;
  readonly transactions?: ReadonlyArray<number>;
  readonly total_price?: string;
  readonly total_shipping_cost?: string;
  readonly discount_amt?: string;
  readonly gift_message?: string;
  readonly grandtotal?: {
    readonly amount: number;
    readonly divisor: number;
    readonly currency_code: string;
  };
};

export type EtsyInboxQueueStatus = 'PENDING' | 'IN_PRODUCTION' | 'CONFIRMED' | 'FULFILLED';

export type EtsyPendingOrder = {
  readonly id: string;
  readonly etsyReceiptId: string;
  readonly shopName: string;
  readonly etsyData: EtsyPendingOrderData;
  readonly importedAt: string;
  readonly selectedForInbox: boolean;
  readonly queueStatus: EtsyInboxQueueStatus;
  readonly matchedAddress: {
    readonly id: string;
    readonly countryCode: string;
    readonly firstName: string;
    readonly lastName: string;
    readonly phone: string;
    readonly street1: string;
    readonly street2?: string | null;
    readonly city: string;
    readonly novaposhtaCityRef?: string | null;
    readonly region: string;
    readonly postalCode: string;
    readonly isDefault: boolean;
  } | null;
  readonly addressReviewStatus: 'PENDING' | 'CONFIRMED' | null;
  readonly addressAssignedAt: string | null;
  readonly addressConfirmedAt: string | null;
  readonly rawShipmentAddressText: string | null;
  readonly imageDownloaded: boolean;
  readonly items: ReadonlyArray<{
    readonly id: string;
    readonly transactionId: string;
    readonly listingId: string;
    readonly listingImageId: string | null;
    readonly title: string;
    readonly quantity: number;
    readonly sku: string | null;
    readonly sizeSelection: string | null;
    readonly materialSelection: string | null;
    readonly resolvedSizeSignal: string | null;
    readonly resolvedPaperSignal: string | null;
    readonly variationSummary: ReadonlyArray<{
      readonly name: string;
      readonly value: string;
    }>;
    readonly imageAvailability: {
      readonly canDownload: boolean;
      readonly kind: 'draft' | 'none';
      readonly status: 'ready' | 'missing' | 'failed';
      readonly note: string;
    };
    readonly previewUrl: string | null;
    readonly imageOriginalWidthPx: number | null;
    readonly imageOriginalHeightPx: number | null;
    readonly fullSizePreviewUrl: string | null;
    readonly fullSizeImageSource: 'MANUAL' | 'REUSED' | null;
    readonly fullSizeImageAssignedAt: string | null;
    readonly fullSizeUploadedFile: {
      readonly id: string;
      readonly filename: string;
      readonly mimeType: string | null;
      readonly sizeBytes: number;
      readonly originalWidthPx: number | null;
      readonly originalHeightPx: number | null;
      readonly dpiX: number | null;
      readonly dpiY: number | null;
    } | null;
    readonly matchedPaperType: {
      readonly id: string;
      readonly name: string;
      readonly brand: string;
    } | null;
    readonly paperAssignmentSource: 'AI' | 'RULE' | 'MANUAL' | null;
    readonly paperReviewStatus: 'PENDING' | 'CONFIRMED' | null;
    readonly paperAssignedAt: string | null;
    readonly paperConfirmedAt: string | null;
    readonly matchedWidthIn: number | null;
    readonly matchedHeightIn: number | null;
    readonly sizeAssignmentSource: 'AI' | 'MANUAL' | null;
    readonly sizeAssignedAt: string | null;
  }>;
  readonly workspaceDraft: {
    readonly id: string;
    readonly status: 'ACTIVE' | 'PARKED' | 'COMPLETED' | 'ARCHIVED';
    readonly updatedAt: string;
    readonly lastOpenedAt: string;
  } | null;
};

export type EtsySyncLog = {
  readonly id: string;
  readonly etsyShopId: string;
  readonly status: EtsySyncLogStatus;
  readonly ordersFound: number;
  readonly ordersImported: number;
  readonly ordersFailed: number;
  readonly errorMessage: string | null;
  readonly startedAt: string;
  readonly finishedAt: string | null;
  readonly durationMs: number | null;
  readonly createdAt: string;
};

export type EtsyPaperMatchingRule = {
  readonly id: string;
  readonly etsyShopId: string | null;
  readonly variationName: string | null;
  readonly sourceKey: string;
  readonly sourceLabel: string;
  readonly notes: string | null;
  readonly lastUsedAt: string | null;
  readonly updatedAt: string;
  readonly paperType: {
    readonly id: string;
    readonly name: string;
    readonly brand: string;
  } | null;
};

type EtsyShopsResponse = {
  readonly data: {
    readonly shops: ReadonlyArray<EtsyShop>;
  };
};

export type EtsyVariationMapping = {
  readonly id: string;
  readonly etsyShopId: string;
  readonly variationName: string;
  readonly variationValue: string;
  readonly normalizedKey: string;
  readonly classification: 'SIZE' | 'PAPER' | 'UNKNOWN';
  readonly classificationSource: 'AI' | 'MANUAL' | 'HEURISTIC' | null;
  readonly classificationConfidence: number | null;
  readonly classificationReason: string | null;
  readonly reviewStatus: 'PENDING' | 'REVIEWED' | 'IGNORED';
  readonly lastClassifiedAt: string | null;
  readonly lastReviewedAt: string | null;
  readonly updatedAt: string;
  readonly sampleValues: ReadonlyArray<string>;
  readonly valueCount: number;
  readonly valueMappings: ReadonlyArray<{
    readonly variationValue: string;
    readonly normalizedKey: string;
    readonly normalizedWidthIn: number | null;
    readonly normalizedHeightIn: number | null;
    readonly normalizedSizeLabel: string | null;
    readonly paperMatchConfidence: number | null;
    readonly paperMatchReason: string | null;
    readonly paperType: {
      readonly id: string;
      readonly name: string;
      readonly brand: string;
    } | null;
  }>;
  readonly etsyShop: {
    readonly id: string;
    readonly shopName: string;
  };
};

type EtsyOAuthStartResponse = {
  readonly data: {
    readonly url: string;
    readonly state: string;
  };
};

type EtsyScopesResponse = {
  readonly data: {
    readonly scopes: ReadonlyArray<string>;
    readonly description: string;
  };
};

type EtsySyncLogsResponse = {
  readonly data: {
    readonly logs: ReadonlyArray<EtsySyncLog>;
  };
};

type EtsyPendingOrdersResponse = {
  readonly data: {
    readonly orders: ReadonlyArray<EtsyPendingOrder>;
  };
};

export type EtsyImportFilters = {
  readonly shopId?: string | null;
  readonly dateFrom?: string | null;
  readonly dateTo?: string | null;
  readonly onlyPaid?: boolean;
  readonly onlyUnshipped?: boolean;
  readonly onlyUndelivered?: boolean;
};

type EtsySyncShopResponse = {
  readonly data: {
    readonly found: number;
    readonly imported: number;
    readonly failed: number;
  };
};

type EtsyPaperMatchingRulesResponse = {
  readonly data: {
    readonly rules: ReadonlyArray<EtsyPaperMatchingRule>;
  };
};

type EtsyPaperMatchingRuleResponse = {
  readonly data: {
    readonly rule: EtsyPaperMatchingRule;
  };
};

type EtsyImportedItemPaperState = {
  readonly id: string;
  readonly matchedPaperTypeId: string | null;
  readonly paperAssignmentSource: 'AI' | 'RULE' | 'MANUAL' | null;
  readonly paperReviewStatus: 'PENDING' | 'CONFIRMED' | null;
  readonly paperAssignedAt: string | null;
  readonly paperConfirmedAt: string | null;
  readonly matchedPaperType: {
    readonly id: string;
    readonly name: string;
    readonly brand: string;
  } | null;
};

type EtsyImportedItemPaperResponse = {
  readonly data: {
    readonly item: EtsyImportedItemPaperState;
    readonly affectedItemCount?: number;
    readonly rule?: EtsyPaperMatchingRule | null;
  };
};

type EtsyImportedItemSizeState = {
  readonly id: string;
  readonly matchedWidthIn: number | null;
  readonly matchedHeightIn: number | null;
  readonly sizeAssignmentSource: 'AI' | 'MANUAL' | null;
  readonly sizeAssignedAt: string | null;
};

type EtsyImportedItemSizeResponse = {
  readonly data: {
    readonly item: EtsyImportedItemSizeState;
  };
};

type EtsyImportedItemFullSizeImageState = {
  readonly id: string;
  readonly fullSizeUploadedFileId: string | null;
  readonly fullSizeImageSource: 'MANUAL' | 'REUSED' | null;
  readonly fullSizeImageAssignedAt: string | null;
  readonly fullSizeUploadedFile: EtsyPendingOrder['items'][number]['fullSizeUploadedFile'];
};

type EtsyImportedItemFullSizeImageResponse = {
  readonly data: {
    readonly item: EtsyImportedItemFullSizeImageState;
    readonly affectedItemCount: number;
    readonly duplicate: boolean;
    readonly file: {
      readonly id: string;
      readonly originalWidthPx?: number;
      readonly originalHeightPx?: number;
      readonly dpiX?: number;
      readonly dpiY?: number;
      readonly widthIn?: number;
      readonly heightIn?: number;
    };
  };
};

type EtsyImportedOrderAddressState = {
  readonly id: string;
  readonly matchedAddressId: string | null;
  readonly addressReviewStatus: 'PENDING' | 'CONFIRMED' | null;
  readonly addressAssignedAt: string | null;
  readonly addressConfirmedAt: string | null;
  readonly matchedAddress: EtsyPendingOrder['matchedAddress'];
};

type EtsyImportedOrderAddressResponse = {
  readonly data: {
    readonly order: EtsyImportedOrderAddressState;
  };
};

export type EtsyParsedShipmentAddress = {
  readonly rawShipmentAddressText: string;
  readonly fields: Partial<Pick<
    ShippingAddress,
    'countryCode' | 'firstName' | 'lastName' | 'phone' | 'street1' | 'street2' | 'city' | 'region' | 'postalCode'
  >>;
  readonly confidence: Record<
    'countryCode' | 'firstName' | 'lastName' | 'phone' | 'street1' | 'street2' | 'city' | 'region' | 'postalCode',
    number
  >;
  readonly reason: string;
};

type EtsyParsedShipmentAddressResponse = {
  readonly data: {
    readonly parsed: EtsyParsedShipmentAddress;
  };
};

type EtsyVariationMappingsResponse = {
  readonly data: {
    readonly mappings: ReadonlyArray<EtsyVariationMapping>;
  };
};

type EtsyVariationMappingResponse = {
  readonly data: {
    readonly mapping: EtsyVariationMapping;
  };
};

type EtsyFetchImportedItemImageResponse = {
  readonly data: {
    readonly importedOrderId: string;
    readonly previewUrl: string | null;
    readonly imageAvailability: {
      readonly canDownload: boolean;
      readonly kind: 'draft' | 'none';
      readonly status: 'ready' | 'missing' | 'failed';
      readonly note: string;
    };
  };
};

type EtsyOpenProductionResponse = {
  readonly data: {
    readonly importedOrder: {
      readonly id: string;
      readonly etsyReceiptId: string;
      readonly shopName: string;
      readonly buyerName: string | null;
    };
    readonly workspaceDraft: WorkspaceDraft | null;
    readonly order: EtsyProductionOrderPayload;
  };
};

export type EtsyProductionOrderPayload = {
  readonly id: string | null;
  readonly status: string;
  readonly packaging: string | null;
  readonly comment: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly shippingAddress: {
    readonly id: string;
    readonly countryCode: string;
    readonly firstName: string;
    readonly lastName: string;
    readonly phone: string;
    readonly street1: string;
    readonly street2: string | null;
    readonly city: string;
    readonly novaposhtaCityRef: string | null;
    readonly novaposhtaWarehouseId: string | null;
    readonly deliveryMode: 'BRANCH' | 'LOCKER' | 'COURIER' | 'PICKUP_POINT' | null;
    readonly shippingProvider: string | null;
    readonly region: string;
    readonly postalCode: string;
    readonly isDefault: boolean;
  } | null;
  readonly paymentInformation: unknown | null;
  readonly items: ReadonlyArray<{
    readonly id: string;
    readonly uploadedFileId: string | null;
    readonly paperTypeId: string | null;
    readonly printerId: string | null;
    readonly widthIn: number | null;
    readonly heightIn: number | null;
    readonly marginIn: number | null;
    readonly colorMode: 'COLOR' | 'BLACK_WHITE' | null;
    readonly status: OrderItemStatus;
    readonly orderId: string | null;
    readonly originalWidthPx: number | null;
    readonly originalHeightPx: number | null;
    readonly dpiX: number | null;
    readonly dpiY: number | null;
    readonly originalWidthIn: number | null;
    readonly originalHeightIn: number | null;
    readonly createdAt: string;
    readonly updatedAt: string;
    readonly priceUsd: number | null;
  }>;
};

export const fetchConnectedEtsyShops = async (): Promise<ReadonlyArray<EtsyShop>> => {
  const response = await api.get<unknown, EtsyShopsResponse>('/etsy/shops');
  return response.data.shops;
};

export const startEtsyOAuth = async (): Promise<{ readonly url: string; readonly state: string }> => {
  const response = await api.get<unknown, EtsyOAuthStartResponse>('/etsy/auth');
  return response.data;
};

export const disconnectEtsyShop = async (shopId: string): Promise<void> => {
  await api.delete(`/etsy/shops/${shopId}`);
};

export const syncEtsyShop = async (
  shopId: string,
  filters: EtsyImportFilters = {},
): Promise<{ readonly found: number; readonly imported: number; readonly failed: number }> => {
  const response = await api.post<unknown, EtsySyncShopResponse>(`/etsy/shops/${shopId}/sync`, filters);
  return response.data;
};

export const fetchEtsySyncLogs = async (shopId: string, limit = 20): Promise<ReadonlyArray<EtsySyncLog>> => {
  const response = await api.get<unknown, EtsySyncLogsResponse>(`/etsy/shops/${shopId}/logs`, {
    params: { limit },
  });
  return response.data.logs;
};

export const fetchEtsyScopes = async (): Promise<{ readonly scopes: ReadonlyArray<string>; readonly description: string }> => {
  const response = await api.get<unknown, EtsyScopesResponse>('/etsy/scopes');
  return response.data;
};

export const fetchPendingEtsyOrders = async (
  options: {
    readonly includeCompleted?: boolean;
  } = {},
): Promise<ReadonlyArray<EtsyPendingOrder>> => {
  const response = await api.get<unknown, EtsyPendingOrdersResponse>('/etsy/pending-orders', {
    params: options.includeCompleted ? { includeCompleted: 'true' } : undefined,
  });
  return response.data.orders;
};

export const fetchImportedEtsyOrders = async (
  filters: EtsyImportFilters & {
    readonly includeCompleted?: boolean;
  } = {},
): Promise<ReadonlyArray<EtsyPendingOrder>> => {
  const params = {
    ...(filters.includeCompleted && { includeCompleted: 'true' }),
    ...(filters.shopId && { shopId: filters.shopId }),
    ...(filters.dateFrom && { dateFrom: filters.dateFrom }),
    ...(filters.dateTo && { dateTo: filters.dateTo }),
    ...(filters.onlyPaid && { onlyPaid: 'true' }),
    ...(filters.onlyUnshipped && { onlyUnshipped: 'true' }),
    ...(filters.onlyUndelivered && { onlyUndelivered: 'true' }),
  };
  const response = await api.get<unknown, EtsyPendingOrdersResponse>('/etsy/imported-orders', {
    params,
  });
  return response.data.orders;
};

export const updateEtsyImportedOrdersInboxSelection = async (
  importedOrderIds: ReadonlyArray<string>,
  selectedForInbox: boolean,
): Promise<{ readonly count: number }> => {
  const response = await api.patch<unknown, { readonly data: { readonly count: number } }>('/etsy/imported-orders/inbox-selection', {
    importedOrderIds,
    selectedForInbox,
  });
  return response.data;
};

export const fetchEtsyPaperMatchingRules = async (): Promise<ReadonlyArray<EtsyPaperMatchingRule>> => {
  const response = await api.get<unknown, EtsyPaperMatchingRulesResponse>('/etsy/matching/paper-rules');
  return response.data.rules;
};

export const fetchEtsyVariationMappings = async (): Promise<ReadonlyArray<EtsyVariationMapping>> => {
  const response = await api.get<unknown, EtsyVariationMappingsResponse>('/etsy/matching/variation-mappings');
  return response.data.mappings;
};

export const classifyAllEtsyVariationMappings = async (): Promise<ReadonlyArray<EtsyVariationMapping>> => {
  const response = await api.post<unknown, EtsyVariationMappingsResponse>('/etsy/matching/variation-mappings/classify');
  return response.data.mappings;
};

export const classifySingleEtsyVariationMapping = async (mappingId: string): Promise<EtsyVariationMapping> => {
  const response = await api.post<unknown, EtsyVariationMappingResponse>(`/etsy/matching/variation-mappings/${mappingId}/classify`);
  return response.data.mapping;
};

export const confirmEtsyVariationMappingClassification = async (mappingId: string): Promise<EtsyVariationMapping> => {
  const response = await api.post<unknown, EtsyVariationMappingResponse>(`/etsy/matching/variation-mappings/${mappingId}/confirm`);
  return response.data.mapping;
};

export const updateEtsyVariationMappingClassification = async (
  mappingId: string,
  classification: 'SIZE' | 'PAPER' | 'UNKNOWN',
): Promise<EtsyVariationMapping> => {
  const response = await api.patch<unknown, EtsyVariationMappingResponse>(`/etsy/matching/variation-mappings/${mappingId}`, {
    classification,
  });
  return response.data.mapping;
};

export const saveEtsyPaperMatchingRule = async (payload: {
  readonly variationName: string;
  readonly sourceLabel: string;
  readonly paperTypeId: string;
  readonly notes?: string | null;
  readonly etsyShopId: string;
}): Promise<EtsyPaperMatchingRule> => {
  const response = await api.post<unknown, EtsyPaperMatchingRuleResponse>('/etsy/matching/paper-rules', payload);
  return response.data.rule;
};

export const saveEtsyImportedItemPaper = async (
  importedItemId: string,
  payload: {
    readonly paperTypeId: string;
    readonly replaceRuleForFuture?: boolean;
  },
): Promise<{
  readonly item: EtsyImportedItemPaperState;
  readonly affectedItemCount: number;
  readonly rule: EtsyPaperMatchingRule | null | undefined;
}> => {
  const response = await api.post<unknown, EtsyImportedItemPaperResponse>(`/etsy/matching/imported-items/${importedItemId}/paper`, payload);
  return {
    item: response.data.item,
    affectedItemCount: response.data.affectedItemCount ?? 1,
    rule: response.data.rule,
  };
};

export const confirmEtsyImportedItemPaper = async (importedItemId: string): Promise<EtsyImportedItemPaperState> => {
  const response = await api.post<unknown, EtsyImportedItemPaperResponse>(`/etsy/matching/imported-items/${importedItemId}/paper/confirm`);
  return response.data.item;
};

export const saveEtsyImportedItemSize = async (
  importedItemId: string,
  payload: {
    readonly widthIn: number;
    readonly heightIn: number;
  },
): Promise<EtsyImportedItemSizeState> => {
  const response = await api.post<unknown, EtsyImportedItemSizeResponse>(`/etsy/matching/imported-items/${importedItemId}/size`, payload);
  return response.data.item;
};

export const uploadEtsyImportedItemFullSizeImage = async (
  importedItemId: string,
  file: File,
): Promise<{
  readonly item: EtsyImportedItemFullSizeImageState;
  readonly affectedItemCount: number;
  readonly duplicate: boolean;
}> => {
  const fileHash = await computeFileSha256(file);
  const contentType = getUploadContentType(file);
  const initResponse = await api.post<{
    readonly completed?: boolean;
    readonly duplicate: boolean;
    readonly uploadUrl?: string;
    readonly objectName?: string;
    readonly requiredHeaders?: Record<string, string>;
    readonly item?: EtsyImportedItemFullSizeImageState;
    readonly affectedItemCount?: number;
  }>(`/etsy/matching/imported-items/${importedItemId}/fullsize-image/direct-init`, {
    filename: file.name,
    contentType,
    sizeBytes: file.size,
    fileHash,
  });

  if (initResponse.data.completed) {
    if (!initResponse.data.item) {
      throw new Error('Direct upload duplicate response did not include an Etsy item.');
    }
    return {
      item: initResponse.data.item,
      affectedItemCount: initResponse.data.affectedItemCount ?? 1,
      duplicate: initResponse.data.duplicate,
    };
  }

  if (!initResponse.data.uploadUrl || !initResponse.data.objectName) {
    throw new Error('Direct upload initialization did not return an upload URL.');
  }

  await uploadFileToSignedUrl(
    initResponse.data.uploadUrl,
    file,
    {
      ...(initResponse.data.requiredHeaders ?? {}),
      'Content-Type': contentType,
    },
  );

  const response = await api.post<unknown, EtsyImportedItemFullSizeImageResponse>(
    `/etsy/matching/imported-items/${importedItemId}/fullsize-image/direct-complete`,
    {
      objectName: initResponse.data.objectName,
      filename: file.name,
      contentType,
      sizeBytes: file.size,
      fileHash,
    },
  );

  return {
    item: response.data.item,
    affectedItemCount: response.data.affectedItemCount,
    duplicate: response.data.duplicate,
  };
};

export const saveEtsyImportedOrderAddress = async (
  importedOrderId: string,
  payload: {
    readonly shippingAddress: ShippingAddress;
  },
): Promise<EtsyImportedOrderAddressState> => {
  const response = await api.post<unknown, EtsyImportedOrderAddressResponse>(`/etsy/matching/imported-orders/${importedOrderId}/address`, payload);
  return response.data.order;
};

export const parseEtsyImportedOrderRawShipmentAddress = async (
  importedOrderId: string,
  rawShipmentAddressText: string,
): Promise<EtsyParsedShipmentAddress> => {
  const response = await api.post<unknown, EtsyParsedShipmentAddressResponse>(
    `/etsy/matching/imported-orders/${importedOrderId}/address/raw-parse`,
    { rawShipmentAddressText },
  );
  return response.data.parsed;
};

export const confirmEtsyImportedOrderAddress = async (
  importedOrderId: string,
): Promise<EtsyImportedOrderAddressState> => {
  const response = await api.post<unknown, EtsyImportedOrderAddressResponse>(`/etsy/matching/imported-orders/${importedOrderId}/address/confirm`);
  return response.data.order;
};

export const fetchEtsyImportedItemImage = async (importedItemId: string): Promise<{
  readonly importedOrderId: string;
  readonly previewUrl: string | null;
  readonly imageAvailability: {
    readonly canDownload: boolean;
    readonly kind: 'draft' | 'none';
    readonly status: 'ready' | 'missing' | 'failed';
    readonly note: string;
  };
}> => {
  const response = await api.post<unknown, EtsyFetchImportedItemImageResponse>(`/etsy/imported-items/${importedItemId}/fetch-image`);
  return response.data;
};

export type EtsyMassMatchResult = {
  readonly uploadedFileId: string;
  readonly filename: string;
  readonly matchedItemId: string | null;
  readonly similarity: number | null;
  readonly affectedItemCount: number;
  readonly status: 'matched' | 'no_match' | 'failed';
  readonly error?: string;
};

export type EtsyMassMatchProgress = {
  readonly jobId: string;
  readonly status: 'queued' | 'processing' | 'completed' | 'failed';
  readonly progress: number;
  readonly draftTotal: number;
  readonly draftProcessed: number;
  readonly uploadTotal: number;
  readonly uploadProcessed: number;
  readonly matchedCount: number;
  readonly failedCount: number;
  readonly productionVectorCount?: number;
  readonly estimatedTimeSeconds: number | null;
  readonly results?: EtsyMassMatchResult[];
  readonly error?: string;
};

export type EtsyDraftVectorStatus = {
  readonly total: number;
  readonly vectorized: number;
  readonly vectorizedDraftItemIds: ReadonlyArray<string>;
};

export type EtsyUploadedImageCandidate = {
  readonly id: string;
  readonly filename: string;
  readonly mimeType: string | null;
  readonly sizeBytes: number;
  readonly originalWidthPx: number | null;
  readonly originalHeightPx: number | null;
  readonly dpiX: number | null;
  readonly dpiY: number | null;
  readonly createdAt: string;
  readonly thumbnailUrl: string;
  readonly visualSimilarity: number | null;
  readonly sizeSimilarity: number | null;
  readonly score: number;
  readonly realPrintSize: {
    readonly widthIn: number;
    readonly heightIn: number;
  } | null;
};

export type EtsyUploadedImageCandidatePage = {
  readonly draft: {
    readonly id: string;
    readonly title: string | null;
    readonly expectedWidthIn: number | null;
    readonly expectedHeightIn: number | null;
    readonly originalWidthPx: number | null;
    readonly originalHeightPx: number | null;
    readonly previewUrl: string | null;
  };
  readonly candidates: ReadonlyArray<EtsyUploadedImageCandidate>;
  readonly page: number;
  readonly pageSize: number;
  readonly total: number;
  readonly totalPages: number;
  readonly showAll: boolean;
  readonly threshold: number;
};

export const getEtsyDraftVectorStatus = async (): Promise<EtsyDraftVectorStatus> => {
  const response = await api.get<EtsyDraftVectorStatus>('/etsy/matching/draft-vector-status');
  return response.data;
};

export const startEtsyDraftVectorization = async (): Promise<EtsyMassMatchProgress> => {
  const response = await api.post<EtsyMassMatchProgress>('/etsy/matching/vectorize-drafts');
  return response.data;
};

export const uploadAndMatchEtsyProductionFiles = async (
  files: ReadonlyArray<File>,
): Promise<EtsyMassMatchProgress> => {
  const directFiles = [];

  for (const file of files) {
    const fileHash = await computeFileSha256(file);
    const contentType = getUploadContentType(file);
    const initResponse = await api.post<{
      readonly duplicate: boolean;
      readonly uploadedFileId?: string;
      readonly uploadUrl?: string;
      readonly objectName?: string;
      readonly requiredHeaders?: Record<string, string>;
    }>('/etsy/matching/mass-upload/direct-init', {
      filename: file.name,
      contentType,
      sizeBytes: file.size,
      fileHash,
    });

    if (initResponse.data.duplicate) {
      if (!initResponse.data.uploadedFileId) {
        throw new Error('Direct upload duplicate response did not include an uploaded file id.');
      }
      directFiles.push({
        filename: file.name,
        contentType,
        sizeBytes: file.size,
        fileHash,
        uploadedFileId: initResponse.data.uploadedFileId,
      });
      continue;
    }

    if (!initResponse.data.uploadUrl || !initResponse.data.objectName) {
      throw new Error('Direct upload initialization did not return an upload URL.');
    }

    await uploadFileToSignedUrl(
      initResponse.data.uploadUrl,
      file,
      {
        ...(initResponse.data.requiredHeaders ?? {}),
        'Content-Type': contentType,
      },
    );

    directFiles.push({
      filename: file.name,
      contentType,
      sizeBytes: file.size,
      fileHash,
      objectName: initResponse.data.objectName,
    });
  }

  const response = await api.post<EtsyMassMatchProgress>('/etsy/matching/mass-upload/direct-complete', {
    files: directFiles,
  });

  return response.data;
};

export const matchExistingEtsyProductionVectors = async (): Promise<EtsyMassMatchProgress> => {
  const response = await api.post<EtsyMassMatchProgress>('/etsy/matching/ai-match-existing');
  return response.data;
};

export const getEtsyMassMatchProgress = async (jobId: string): Promise<EtsyMassMatchProgress> => {
  const response = await api.get<EtsyMassMatchProgress>('/etsy/matching/mass-match-progress', {
    params: { jobId },
  });
  return response.data;
};

export const fetchEtsyUploadedImageCandidates = async (
  importedItemId: string,
  options: {
    readonly page: number;
    readonly pageSize: number;
    readonly showAll: boolean;
  },
): Promise<EtsyUploadedImageCandidatePage> => {
  const response = await api.get<unknown, { readonly data: EtsyUploadedImageCandidatePage }>(
    `/etsy/matching/imported-items/${importedItemId}/uploaded-image-candidates`,
    {
      params: {
        page: options.page,
        pageSize: options.pageSize,
        showAll: options.showAll ? 'true' : 'false',
      },
    },
  );
  return response.data;
};

export const assignImportedItemFullSizeImage = async (payload: {
  readonly userId: string;
  readonly importedItemId: string;
  readonly uploadedFileId: string;
}): Promise<EtsyImportedItemFullSizeImageResponse['data']> => {
  const response = await api.post<unknown, EtsyImportedItemFullSizeImageResponse>(
    `/etsy/matching/imported-items/${payload.importedItemId}/assign-image`,
    payload
  );
  const responseData = response.data as EtsyImportedItemFullSizeImageResponse['data'] & {
    readonly data?: EtsyImportedItemFullSizeImageResponse['data'];
  };
  return responseData.data ?? responseData;
};

export const deleteEtsyImportedOrder = async (importedOrderId: string): Promise<void> => {
  await api.delete(`/etsy/imported-orders/${importedOrderId}`);
};

export const openEtsyImportedOrderInProduction = async (
  importedOrderId: string,
  payload: {
    readonly serviceId?: string | null;
  } = {},
): Promise<{
  readonly importedOrder: EtsyOpenProductionResponse['data']['importedOrder'];
  readonly workspaceDraft: WorkspaceDraft | null;
  readonly order: EtsyProductionOrderPayload;
}> => {
  const response = await api.post<unknown, EtsyOpenProductionResponse>(
    `/etsy/imported-orders/${importedOrderId}/open-production`,
    payload,
  );

  return response.data;
};
